/**
 * Adyen App — Update Payment Amount
 *
 * Server-side proxy to the Saleor Adyen app's `payment/update_amount` endpoint.
 * Used during PayPal express checkout when shipping changes or additional
 * payment details require refreshing the Adyen `payment_data` token.
 *
 * Requires `ADYEN_APP_API_URI` to be set in the server environment.
 *
 * @see app/api/checkout/express/update-adyen-payment/route.ts
 * @see lib/checkout/payments/adyen/updateAdyenPaymentClient.ts
 * @see breitling-storefront-next/src/lib/adyen.ts (legacy client-direct call)
 */

import { devError } from '@/lib/utils/devLog';

const ADYEN_APP_API_URI = process.env.ADYEN_APP_API_URI || '';

/** Successful Adyen app response with refreshed `payment_data`. */
export interface UpdateAdyenPaymentAmountSuccess {
  payment_data: string;
}

/** Adyen app or transport failure returned as a stable `{ code, msg }` pair. */
export interface UpdateAdyenPaymentAmountError {
  code: number;
  msg: string;
}

/**
 * Discriminated union result. Narrow with `'payment_data' in result` or
 * `isUpdateAdyenPaymentAmountError(result)` for the error branch.
 */
export type UpdateAdyenPaymentAmountResult =
  | UpdateAdyenPaymentAmountSuccess
  | UpdateAdyenPaymentAmountError;

/** Ensures the Adyen app base URL ends with a trailing slash. */
function normalizeAdyenAppBaseUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
}

/** Type guard for Adyen app error payloads (`{ code, msg }` without `payment_data`). */
export function isUpdateAdyenPaymentAmountError(
  result: UpdateAdyenPaymentAmountResult
): result is UpdateAdyenPaymentAmountError {
  return 'code' in result && 'msg' in result && !('payment_data' in result);
}

/**
 * Calls the Adyen Saleor app to refresh payment data after cart/shipping changes.
 *
 * Posts to `{ADYEN_APP_API_URI}payment/update_amount` with snake_case body keys
 * (`transaction_id`, `payment_data`) as expected by the Adyen app.
 *
 * @param transactionId - Saleor transaction ID (PSP session reference)
 * @param paymentData - Current Adyen `paymentData` string from the wallet component
 * @returns Refreshed `payment_data` on success, or `{ code, msg }` on failure
 *
 * @example
 * ```ts
 * const result = await updateAdyenPaymentAmount('txn_123', 'Ab02b4c0!');
 * if ('payment_data' in result) {
 *   await component.updatePaymentData(result.payment_data);
 * } else {
 *   devError('Adyen update failed:', result.msg);
 * }
 * ```
 */
export async function updateAdyenPaymentAmount(
  transactionId: string,
  paymentData: string
): Promise<UpdateAdyenPaymentAmountResult> {
  const baseUrl = normalizeAdyenAppBaseUrl(ADYEN_APP_API_URI);

  if (!baseUrl) {
    devError('[updateAdyenPaymentAmount] ADYEN_APP_API_URI is not configured');
    return { code: -1, msg: 'Adyen app API is not configured' };
  }

  try {
    const response = await fetch(`${baseUrl}payment/update_amount`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        transaction_id: transactionId,
        payment_data: paymentData,
      }),
    });

    if (!response.ok) {
      const msg = `HTTP error: ${response.status} ${response.statusText}`;
      devError('[updateAdyenPaymentAmount] HTTP error:', { status: response.status, msg });
      return { code: response.status, msg };
    }

    const json = (await response.json()) as UpdateAdyenPaymentAmountResult;

    if (isUpdateAdyenPaymentAmountError(json)) {
      devError('[updateAdyenPaymentAmount] Adyen app error:', json);
      return json;
    }

    if (!json.payment_data) {
      devError('[updateAdyenPaymentAmount] Adyen app response missing payment_data');
      return { code: -1, msg: 'Adyen app response missing payment_data' };
    }

    return { payment_data: json.payment_data };
  } catch (error) {
    devError('[updateAdyenPaymentAmount] Request failed:', error);
    return { code: -1, msg: 'Failed to update the payment amount' };
  }
}
